import { TFunction } from 'i18next';
import { UserMergeRequestListItem } from '../types';
import {
  Callout,
  DataTableMeta,
  DateColumn,
  LimitedContent,
  Typography,
} from '@otp/ui';
import { AdminActionsField } from './columns/admin-actions';
import { StatusColumn } from './columns/status';
import { ProfileActionsField } from './columns/profile-actions';
import { UsernameEmail } from 'src/components/manage-otps/list/table/columns/username-email';

export const getTableConfig = (
  data: UserMergeRequestListItem[],
  { t }: { t: TFunction },
  hiddenColumns?: string[],
) =>
  [
    {
      name: 'createdAt',
      label: t('user-merge-requests.list.fields.date-requested'),
      options: {
        customBodyRender: (value: string) => <DateColumn date={value} />,
      },
    },
    {
      name: 'user',
      label: t('user-merge-requests.list.fields.user'),
      options: {
        customBodyRender: (_: string, column: DataTableMeta) => {
          const entity = data[column.rowIndex];

          return (
            <UsernameEmail
              user={entity.user}
              options={{ withLevel: true, withMcId: true, withPrograms: true }}
            />
          );
        },
      },
    },
    {
      name: 'eIdsToMerge',
      label: t('user-merge-requests.list.fields.eids-to-merge'),
      options: {
        customBodyRender: (value: string, column: DataTableMeta) => {
          const entity = data[column.rowIndex];
          const usersToMerge = entity.usersToMerge;
          const eIdsToMerge = entity.eIdsToMerge;
          const missingEIds = eIdsToMerge
            .filter((eid) => !usersToMerge.some((user) => user.mcId === eid))
            .join(', ');

          return (
            <div className="flex flex-col gap-1">
              {usersToMerge.map((user) => (
                <UsernameEmail
                  key={user.id}
                  user={user}
                  options={{
                    withLevel: true,
                    withMcId: true,
                    withPrograms: true,
                  }}
                />
              ))}
              {missingEIds && (
                <Callout color="warning" icon={false} className="p-2">
                  <Typography variant="subtitle1" color="error">
                    {t(
                      'user-merge-requests.list.fields.missing-eids-to-merge',
                      { missingEIds },
                    )}
                  </Typography>
                </Callout>
              )}
            </div>
          );
        },
      },
    },
    {
      name: 'status',
      label: t('user-merge-requests.list.fields.status'),
      options: {
        customBodyRender: (value: string, column: DataTableMeta) => {
          const entity = data[column.rowIndex];

          return <StatusColumn status={entity.status} />;
        },
      },
    },
    {
      name: 'notes',
      label: t('user-merge-requests.list.fields.notes'),
      options: {
        customBodyRender: (_: any, column: DataTableMeta) => {
          const entity = data[column.rowIndex];
          const requestNotes = entity.requestNotes || '-';
          const responseNotes = entity.responseNotes;

          return (
            <>
              <Typography variant="subtitle1" fontWeight="bold">
                {t('user-merge-requests.list.fields.request-notes')}
              </Typography>
              <LimitedContent maxRows={3}>
                <div dangerouslySetInnerHTML={{ __html: requestNotes }} />
              </LimitedContent>
              {responseNotes && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('user-merge-requests.list.fields.response-notes')}
                  </Typography>
                  <LimitedContent maxRows={3}>
                    <div dangerouslySetInnerHTML={{ __html: responseNotes }} />
                  </LimitedContent>
                </>
              )}
            </>
          );
        },
      },
    },
    {
      name: 'adminActions',
      label: t('common.actions'),
      options: {
        customBodyRender: (_: any, column: DataTableMeta) => {
          const entity = data[column.rowIndex];

          return <AdminActionsField entity={entity} />;
        },
        setCellHeaderProps: () => ({ className: 'text-center' }),
      },
    },
    {
      name: 'profileActions',
      label: t('common.actions'),
      options: {
        customBodyRender: (_: any, column: DataTableMeta) => {
          const entity = data[column.rowIndex];

          return <ProfileActionsField entity={entity} />;
        },
        setCellHeaderProps: () => ({ className: 'text-center' }),
      },
    },
  ].filter((column) => !hiddenColumns?.includes(column.name));
